import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import NewHeader from "../../Home/NewHeader";
import {
  useLazyGetQuestListPerLocationQuery,
  useLazyGetQuestListQuery,
  usePostFavoriteLocationMutation,
} from "../../../services/apiSlice";
import ImageSlider from "../../../components/common/SlideShowImage/ImageSlider";
import CardQuestPerType from "../../../components/common/Card/CardQuestPerType";
import images from "../../../images";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { toast } from "react-toastify";
import Button from "../../../components/common/Button/Button";
import { usePostHog } from "../../../hooks/usePostHog";

// Constants
const API_LIMIT = 5;
const API_LIMIT_STEP = 5;

/**
 * QuestPerLocation component displays quests for a specific location with search, favorite, and infinite scroll functionality
 */
const QuestPerLocation = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackEvent } = usePostHog();
  const { id: locationId } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search") || "";
  const accessToken =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  const [quests, setQuests] = useState([]);
  const [favorite, setFavorite] = useState(false);
  const [dataQuestLocation, setDataQuestLocation] = useState();
  const [isParticipantsCompleted, setIsParticipantsCompleted] = useState(false);

  const [limit, setLimit] = useState(API_LIMIT);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const infomationBizModalRef = useRef(null);
  const [
    getListQuestsPerLocation,
    { isFetching: isFetchingPerLocation, error: errorPerLocation },
  ] = useLazyGetQuestListPerLocationQuery({
    skip: accessToken,
  });
  const [getListQuests, { isFetching: isFetchingAuth, error: errorAuth }] =
    useLazyGetQuestListQuery({
      skip: !accessToken,
    });
  const [favorites, { isLoading: isFavoriteLoading }] =
    usePostFavoriteLocationMutation();

  // Load Google Maps API
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const checkParticipantsStatus = useCallback((data) => {
    if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) {
      setIsParticipantsCompleted(false);
      return;
    }

    const allQuestsCompleted = data.data
      .filter((quest) => quest.quest_business.quota_type === "DAILY")
      .every(
        (quest) =>
          quest.participants &&
          Array.isArray(quest.participants) &&
          quest.participants.length > 0 &&
          quest.participants.length === 2 &&
          quest.participants.every(
            (participant) => participant.status === "COMPLETED"
          )
      );

    setIsParticipantsCompleted(allQuestsCompleted);
  }, []);

  const fetchQuests = useCallback(
    async (newLimit, append = false) => {
      const params = {
        limit: newLimit,
        id_location: locationId,
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
      };

      try {
        const response = accessToken
          ? await getListQuests(params).unwrap()
          : await getListQuestsPerLocation(params).unwrap();

        setFavorite(response.favorited);
        setQuests((prev) =>
          append ? [...prev, ...(response?.data || [])] : response?.data || []
        );
        setDataQuestLocation((prev) =>
          append ? [...prev, ...(response || [])] : response || []
        );
        setTotal(response?.total || 0);
        setHasMore(newLimit < response?.total);
        checkParticipantsStatus(response);

        // Track location quest view and results
        if (!append) { // Only track initial fetch, not infinite scroll
          trackEvent('location_quests_viewed', {
            location_id: locationId,
            search_query: searchQuery.trim() || null,
            quest_count: response?.data?.length || 0,
            total_quests_available: response?.total || 0,
            has_search_filter: !!searchQuery.trim(),
            is_favorited: response.favorited,
            user_authenticated: !!accessToken
          });
        }
      } catch (error) {
        console.error("Error fetching quests:", error);
        
        trackEvent('location_quests_fetch_error', {
          location_id: locationId,
          search_query: searchQuery.trim() || null,
          error_message: error.message || 'Unknown error',
          user_authenticated: !!accessToken
        });
      }
    },
    [
      accessToken,
      locationId,
      searchQuery,
      getListQuests,
      getListQuestsPerLocation,
      checkParticipantsStatus,
      trackEvent,
    ]
  );

  const handleQuestSaved = useCallback(
    (questId, status) => {
      setQuests([]);
      setLimit(API_LIMIT);
      setHasMore(true);
      fetchQuests(API_LIMIT, false);
    },
    [fetchQuests]
  );

  useEffect(() => {
    setQuests([]);
    setLimit(API_LIMIT);
    setHasMore(true);
    fetchQuests(API_LIMIT, false);
  }, [searchQuery, locationId, fetchQuests, dataQuestLocation]);

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100 &&
      hasMore &&
      !isFetchingPerLocation &&
      !isFetchingAuth
    ) {
      setLimit((prev) => {
        const nextLimit = prev + API_LIMIT_STEP;
        fetchQuests(nextLimit, false);
        return nextLimit;
      });
    }
  }, [hasMore, isFetchingPerLocation, isFetchingAuth, fetchQuests]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll, limit, total]);

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NewHeader />
        <div className="container mx-auto px-4 py-6">
          <div>
            {t("quest.questDetails.errorLoadingMap") || "Error loading map"}
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NewHeader />
        <div className="container mx-auto px-4 py-6">
          <LoadingCommon statusLoading={true} />
        </div>
      </div>
    );
  }

  if (errorPerLocation || errorAuth) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NewHeader />
        <div className="flex flex-col items-center justify-center p-4 ">
          <div className=" mx-auto px-4 py-6">
            {t("quest.questDetails.errLoadingQuest")}
          </div>
          <hr className="my-[30px]" />
          <Button
            text={"Explore more quests"}
            onClick={() => {
              navigate("/");
            }}
            className={
              "rounded-md border-solid border-[#253FF6] border-[1.5px] text-[#253FF6] text-xl font-medium"
            }
          />
        </div>
      </div>
    );
  }

  if (!quests || quests.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NewHeader />
        <div className="container mx-auto px-4 py-6">
          <LoadingCommon statusLoading={true} />
        </div>
      </div>
    );
  }

  const handleSignIn = () => {
    navigate("/sign-in?from=qrcode&location_id=" + locationId);
  };

  const handleSignUp = () => {
    navigate("/register?from=qrcode&location_id=" + locationId);
  };

  const handleFavoriteLocation = async (locationId) => {
    try {
      const respon = await favorites({
        location_id: locationId,
      }).unwrap();

      if (respon.status === 200) {
        fetchQuests();
      }
    } catch (error) {
      console.error("Failed to favorite location:", error);
    }
  };

  const isIntroCompleted = quests.some(
    (quest) =>
      quest.quest_business.quest_type === "INTRODUCE" &&
      quest.consumer_status === "COMPLETED"
  );

  const firstQuest = quests[0] || {};

  const questWithThumbnail =
    quests.find(
      (quest) =>
        quest.thumbnails &&
        Array.isArray(quest.thumbnails) &&
        quest.thumbnails.length > 0 &&
        quest.thumbnails.some((thumbnail) => thumbnail.file_path)
    ) || firstQuest;

  const companyName = firstQuest.quest_business?.business?.company_name;
  const address = firstQuest.location?.address || "";
  const locationName = firstQuest.location?.name || "";

  const phone = firstQuest.quest_business?.business?.phone || "";

  const defaultProps = {
    center: { lat: 10.99835602, lng: 77.01502627 },
    zoom: 11,
  };
  const containerStyle = {
    width: "100%",
    height: "180px",
    borderRadius: "12px",
    filter: "#EEEAE8",
    transition: "all 0.3s ease-in-out",
  };
  const mapCenter =
    firstQuest?.location?.latitude && firstQuest?.location?.longitude
      ? {
          lat: Number(firstQuest.location.latitude),
          lng: Number(firstQuest.location.longitude),
        }
      : defaultProps.center;

  const handleOpenMaps = () => {
    const latitude = firstQuest?.location?.latitude;
    const longitude = firstQuest?.location?.longitude;

    if (!latitude || !longitude) {
      toast.error(t("quest.questDetails.locationNotAvailable"));
      return;
    }

    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    const appleMapsUrl = `http://maps.apple.com/?q=${latitude},${longitude}&ll=${latitude},${longitude}`;

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      const link = document.createElement("a");
      link.href = appleMapsUrl;
      link.click();

      setTimeout(() => {
        if (document.hasFocus()) {
          window.open(googleMapsUrl, "_blank");
        }
      }, 1500);
    } else if (/android/i.test(userAgent)) {
      window.open(googleMapsUrl, "_blank");
    } else {
      window.open(googleMapsUrl, "_blank");
    }
  };

  const groupByHours = () => {
    const workingWeek = firstQuest.quest_business?.business.working_week || [];

    if (!Array.isArray(workingWeek) || workingWeek.length === 0) {
      return [];
    }

    const groups = [];
    let currentGroup = [workingWeek[0]];

    for (let i = 1; i < workingWeek.length; i++) {
      const prev = workingWeek[i - 1];
      const curr = workingWeek[i];
      if (
        prev.is_closed === curr.is_closed &&
        prev.working_day[0]?.start_at === curr.working_day[0]?.start_at &&
        prev.working_day[0]?.end_at === curr.working_day[0]?.end_at
      ) {
        currentGroup.push(curr);
      } else {
        groups.push(currentGroup);
        currentGroup = [curr];
      }
    }
    groups.push(currentGroup);

    const areAllDaysUniform = workingWeek.length > 0 && groups.length === 1;

    const formattedGroups = groups.map((group) => {
      const days = group.map((d) => d.weekday);
      if (areAllDaysUniform) {
        return {
          days: "Monday - Sunday",
          startTime: group[0].working_day[0]?.start_at?.slice(0, 5) || "N/A",
          endTime: group[0].working_day[0]?.end_at?.slice(0, 5) || "N/A",
        };
      } else if (
        days.length === 5 &&
        days.every(
          (d, i) =>
            d === workingWeek[i].weekday &&
            workingWeek[i].weekday ===
              ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"][i]
        )
      ) {
        return {
          days: "Mon - Fri",
          startTime: group[0].working_day[0]?.start_at?.slice(0, 5) || "N/A",
          endTime: group[0].working_day[0]?.end_at?.slice(0, 5) || "N/A",
        };
      }
      return {
        days: days.join(" - "),
        startTime: group[0].working_day[0]?.start_at?.slice(0, 5) || "N/A",
        endTime: group[0].working_day[0]?.end_at?.slice(0, 5) || "N/A",
      };
    });

    return formattedGroups;
  };

  const formattedGroups = groupByHours();

  return (
    <div className="flex flex-col mx-auto w-full max-w-[480px]">
      <div className="sticky top-0 left-0 w-full bg-white z-50">
        <NewHeader handleSignIn={handleSignIn} handleSignUp={handleSignUp} />
      </div>
      <div className="w-full relative rounded-md">
        <ImageSlider
          images={
            questWithThumbnail.thumbnails
              ?.filter((t) => t.file_path)
              .map((t) => t.file_path) || []
          }
          imageHeight="h-[125.667px]"
          applyImageOverlay={false}
          className="rounded-none"
        />
        <div
          className={`flex absolute top-[68.27px] left-[30px] flex-col self-start text-xs font-bold text-center text-black rounded-none h-[70px] w-[70px] z-20`}
        >
          <figure className="flex shrink-0 self-stretch my-auto" />
          <div className="w-[70px] h-[70px] rounded-md bg-gray-100 flex items-center justify-center">
            <img
              src={`${process.env.REACT_APP_URL_IMAGE}/${firstQuest?.quest_business?.business?.avatar_data?.file_path}`}
              className="object-contain"
              alt=""
              style={{
                imageRendering: "-webkit-optimize-contrast",
                filter: "contrast(1.1)",
              }}
            />
          </div>
        </div>
      </div>
      <div className="container mx-auto px-[30px] mt-[44px]">
        <div className="flex flex-col font-medium gap-[6px]">
          <div className="flex flex-row items-center justify-between">
            <span className="text-2xl text-[#333333]">{companyName}</span>
            <div
              onClick={() =>
                accessToken
                  ? handleFavoriteLocation(locationId)
                  : navigate("/register?from=qrcode&quest_id=" + locationId)
              }
            >
              <img
                src={
                  favorite === true
                    ? images.Heart02ActiveIcon
                    : images.HeartGrayIcon
                }
                alt="heart-icon"
                className="w-[24px] h-[24px]"
              />
            </div>
          </div>
          <div>
            <p className="text-[#333333] text-[16px] font-medium">
              {firstQuest.quest_business?.business.description}
            </p>
          </div>
          <div
            className="flex flex-row items-start gap-1 mt-3"
            onClick={() => infomationBizModalRef?.current?.openModal()}
          >
            <img
              src={images.MapPinIcon}
              alt="map-pin-icon"
              className="h-[18px] w-[18px]"
            />
            <span className="text-base text-[#A0A0A0] leading-none">
              {locationName || "Location Name"}
            </span>
          </div>
        </div>

        {quests.every((quest) => quest?.consumer_status === "COMPLETED") &&
        isParticipantsCompleted === true ? (
          <div className="flex flex-col justify-center items-center text-center mt-4 gap-6 w-full">
            <div className="flex justify-center items-center">
              <img
                src={images.CompleteQuestImagePerLocation}
                alt="no-quests"
                className="w-[170px]"
              />
            </div>
            <div className="flex flex-col justify-center font-medium text-2xl w-full text-center text-[#253FF6]">
              <p>{t("quest.completedQuest.youveCompletedAllTheQuests")}</p>
            </div>
            <div className="text-[#333333] text-lg">
              <p>{t("quest.completedQuest.awesomeJob")}</p>
            </div>
          </div>
        ) : (
          <></>
        )}

        <div className="flex flex-col gap-3 my-[30px]">
          <div className="text-[#253FF6] font-bold text-2xl">
            {t("home.quest")}
          </div>
        </div>
        {quests.length === 0 && !isFetchingPerLocation && !isFetchingAuth ? (
          <div className="flex flex-col justify-center items-center text-center mt-4 gap-6 w-full">
            <div className="flex justify-center items-center">
              <img
                src={images.ReviewReceiptImage}
                alt="no-quests"
                className="w-[170px]"
              />
            </div>
            <div className="flex flex-col justify-center font-medium text-2xl w-full text-center text-[#253FF6]">
              <p>{t("home.wereSorry")}</p>
            </div>
            <div className="text-[#333333] text-lg">
              <p>{t("home.thereAreNoQuestsPerLocation")}</p>
            </div>
            <div className="text-[#A0A0A0] text-sm">
              <p>{t("home.pleaseTryAgainSoon")}</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {quests
              ?.slice()
              .sort((a, b) => {
                if (
                  a.consumer_status === "COMPLETED" &&
                  b.consumer_status !== "COMPLETED"
                )
                  return 1;
                if (
                  a.consumer_status !== "COMPLETED" &&
                  b.consumer_status === "COMPLETED"
                )
                  return -1;
                return 0;
              })
              .map((quest) => (
                <CardQuestPerType
                  key={quest.id}
                  slotAvailable={quest.slot_available}
                  consumerStatus={
                    isParticipantsCompleted === false &&
                    quest.quest_business.quota_type === "DAILY"
                      ? "NOT_STARTED"
                      : quest?.consumer_status
                  }
                  questType={quest.quest_business.quest_type}
                  rallyCoin={quest.quest_business.token}
                  isIntroCompleted={isIntroCompleted}
                  isEnable={quest.is_enable}
                  expireTime={
                    quest.participants &&
                    Array.isArray(quest.participants) &&
                    quest.participants.length > 0
                      ? quest.participants.find(
                          (participant) => participant.status === "PAUSED_QUEST"
                        )?.expired_at
                      : null
                  }
                  participantId={
                    quest.participants &&
                    Array.isArray(quest.participants) &&
                    quest.participants.length > 0
                      ? quest.participants.find(
                          (participant) => participant.status === "PAUSED_QUEST"
                        )?.id
                      : null
                  }
                  questId={quest.id}
                  onClick={() => {
                    const currentSearch = searchParams.get("search");
                    const navigateUrl = `/quest/details/${quest.id}${
                      currentSearch
                        ? `?search=${encodeURIComponent(currentSearch)}`
                        : ""
                    }`;
                    navigate(navigateUrl);
                  }}
                  onQuestSaved={handleQuestSaved}
                  isParticipantsCompleted={isParticipantsCompleted}
                />
              ))}
          </div>
        )}

        <hr className="my-[30px]" />
        <Button
          text={"Explore more quests"}
          onClick={() => {
            navigate("/");
          }}
          className={
            "rounded-md border-solid border-[#253FF6] border-[1.5px] text-[#253FF6] text-xl font-medium"
          }
        />
        {(isFetchingPerLocation || isFetchingAuth || isFavoriteLoading) && (
          <div className="text-center py-4">
            <LoadingCommon statusLoading={true} />
          </div>
        )}
      </div>

      <CommonModal
        ref={infomationBizModalRef}
        classNameText="text-[#201D1B] font-medium text-lg"
        canClickOverLay={false}
        showCloseIcon={true}
        closeIcon={images.CloseModalIconGray}
      >
        <div className="flex flex-col justify-start items-start gap-[6px] w-full">
          <div className="flex flex-col justify-start items-start gap-[6px]">
            <p className="text-[#333333] text-[16px] font-medium">
              {companyName}
            </p>
            {locationName && (
              <div className="flex flex-row items-start gap-1 text-start">
                <img
                  src={images.MapPinIcon}
                  alt="map-pin-icon"
                  className="h-[18px] w-[18px]"
                />
                <span className="text-base text-[#A0A0A0] leading-none">
                  {locationName}
                </span>
              </div>
            )}
          </div>

          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={defaultProps.zoom}
            onUnmount={(map) => {}}
          >
            {firstQuest?.location?.latitude &&
              firstQuest?.location?.longitude && (
                <Marker
                  position={{
                    lat: Number(firstQuest.location.latitude),
                    lng: Number(firstQuest.location.longitude),
                  }}
                  icon={{
                    url: images.DotmapBlueIcon,
                    scaledSize: new window.google.maps.Size(40, 40),
                  }}
                />
              )}
          </GoogleMap>

          <div className="flex flex-col justify-between w-full gap-3 mt-2">
            <div className="flex justify-start text-start gap-1.5 ">
              <img src={images.SendIcon} alt="" className="w-[18px] h-[18px]" />
              <p className="text-[#A0A0A0]">{address}</p>
            </div>
            {phone && (
              <div className="flex justify-start text-start gap-1.5 ">
                <img
                  src={images.PhoneIconSVG}
                  alt=""
                  className="w-[18px] h-[18px]"
                />
                <p className="text-[#A0A0A0]">{phone}</p>
              </div>
            )}

            <div className="w-full">
              <div className="flex flex-col gap-1">
                {formattedGroups.length > 0 &&
                  formattedGroups.map((group, index) => (
                    <div
                      key={index}
                      className="flex justify-start gap-2 text-[#A0A0A0] text-sm"
                    >
                      <img
                        src={images.OclockIconSVG}
                        className="w-[18px] h-[18px]"
                        alt=""
                      />
                      <span>{group.days}:</span>
                      <span>{`${group.startTime} - ${group.endTime}`}</span>
                    </div>
                  ))}
              </div>
            </div>
            <div
              className="flex justify-start gap-1.5 cursor-pointer"
              onClick={handleOpenMaps}
            >
              <img
                src={images.MapIcon2Blue}
                alt=""
                className="w-[18px] h-[18px]"
              />
              <p className="text-[#253FF6] text-sm font-medium">
                {t("quest.questDetails.openInMaps")}
              </p>
            </div>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default QuestPerLocation;
